<template>
  <ion-content class="wizard-scroll">
    <div class="wizard">

      <div class="wizard-cabecera">
        <div>
          <h2 class="wizard-titulo">Nuevo siniestro</h2>
          <div class="wizard-pasos">
            <span :class="['wizard-paso', step === 1 && 'wizard-paso-activo']">1 · Afectados</span>
            <span class="wizard-paso-sep">›</span>
            <span :class="['wizard-paso', step === 2 && 'wizard-paso-activo']">2 · Datos del siniestro</span>
          </div>
        </div>
        <button type="button" class="wizard-cerrar" @click="closeModal()">
          <ion-icon :icon="close"></ion-icon>
        </button>
      </div>

      <!-- Borrador recuperable -->
      <div v-if="borradorDisponible" class="aviso aviso-borrador">
        <div>
          Hay un borrador sin enviar del {{ borradorDisponible.fechaTexto }}.
        </div>
        <div class="aviso-acciones">
          <button type="button" class="btn-mini" @click="restaurarBorrador">Recuperar</button>
          <button type="button" class="btn-mini btn-mini-gris" @click="descartarBorrador">Descartar</button>
        </div>
      </div>

      <!-- Informe de error concreto -->
      <div v-if="reporteError" class="aviso aviso-error">
        <div class="aviso-error-titulo">
          No se pudo guardar el siniestro ({{ reporteError.hora }})
        </div>
        <ul class="aviso-error-lista">
          <li v-for="(m, i) in reporteError.mensajes" :key="i">{{ m }}</li>
        </ul>
        <div class="aviso-nota">
          Los datos introducidos se conservan como borrador: puedes corregir y reenviar sin volver a escribirlos.
        </div>
        <div class="aviso-acciones">
          <button type="button" class="btn-mini" @click="copiarReporte">
            {{ reporteCopiado ? 'Copiado ✓' : 'Copiar informe' }}
          </button>
          <button type="button" class="btn-mini btn-mini-gris" @click="reporteError = null">Cerrar</button>
        </div>
      </div>

      <!-- Avisos de validación local -->
      <div v-if="faltan.length" class="aviso aviso-faltan">
        <div class="aviso-error-titulo">Faltan datos:</div>
        <ul class="aviso-error-lista">
          <li v-for="(m, i) in faltan" :key="i">{{ m }}</li>
        </ul>
      </div>

      <!-- PASO 1: afectados -->
      <div v-if="step === 1">
        <div v-for="(cliente, idx) in clientes" :key="idx" class="tarjeta-afectado">
          <div class="tarjeta-cabecera">
            <span class="tarjeta-titulo">Afectado {{ idx + 1 }}</span>
            <span v-if="origenAutofill[idx]" class="chip-autofill">
              {{ origenAutofill[idx] === 'reclasin' ? 'Datos de ficha existente' : 'Datos traídos de EPLAN' }}
            </span>
            <button v-if="clientes.length > 1" type="button" class="btn-mini btn-mini-gris" @click="quitarAfectado(idx)">
              Quitar
            </button>
          </div>

          <div class="grid-campos">
            <div class="campo">
              <label>DNI / NIE *</label>
              <div class="campo-dni">
                <input
                  type="text"
                  v-model="cliente.dni"
                  maxlength="9"
                  placeholder="12345678Z"
                  :class="dniEstado[idx] === false && 'campo-invalido'"
                  @blur="onDniBlur(idx)"
                  @input="cliente.dni = cliente.dni.toUpperCase()"
                />
                <span v-if="dniEstado[idx] === true" class="dni-ok">✓</span>
                <span v-if="dniEstado[idx] === false" class="dni-mal" title="La letra no corresponde con el número">✗ letra incorrecta</span>
                <span v-if="buscandoDni === idx" class="dni-buscando">buscando…</span>
              </div>
            </div>
            <div class="campo">
              <label>Nombre y apellidos *</label>
              <input type="text" v-model="cliente.nombre" placeholder="Nombre completo" />
            </div>
            <div class="campo">
              <label>Móvil *</label>
              <input type="tel" v-model="cliente.movil" placeholder="600000000" />
            </div>
            <div class="campo">
              <label>Teléfono *</label>
              <input type="tel" v-model="cliente.telefono" placeholder="956000000" />
            </div>
            <div class="campo">
              <label>Nº de póliza *</label>
              <input type="text" v-model="cliente.num_poliza" placeholder="Número de póliza" />
            </div>
            <div class="campo">
              <label>Compañía *</label>
              <select v-model="cliente.compania_id">
                <option value="" disabled>Selecciona compañía</option>
                <option v-for="compania in companias" :value="compania.id" :key="compania.id">
                  {{ compania.nombre }}
                </option>
              </select>
            </div>
            <div class="campo">
              <label>Población *</label>
              <input type="text" v-model="cliente.poblacion" placeholder="Población" />
            </div>
            <div class="campo">
              <label>Dirección</label>
              <input type="text" v-model="cliente.direccion" placeholder="Calle, número…" />
            </div>
          </div>
        </div>

        <button type="button" class="btn-anadir" @click="anadirAfectado">+ Añadir otro afectado</button>

        <div class="wizard-botonera">
          <span></span>
          <ion-button color="dark" @click="irAPaso2">Siguiente</ion-button>
        </div>
      </div>

      <!-- PASO 2: datos del siniestro -->
      <div v-if="step === 2">
        <div class="grid-campos">
          <div v-if="getUser?.role?.name == 'Despacho'" class="campo campo-ancho">
            <label>Colaborador *</label>
            <select v-model="colaborador_id">
              <option :value="null" disabled>Selecciona colaborador</option>
              <option v-for="colaborador in colaboradores" :value="colaborador.id" :key="colaborador.id">
                {{ colaborador.name == null ? colaborador.user_name : colaborador.name }}
              </option>
            </select>
          </div>
          <div class="campo">
            <label>Fecha del siniestro *</label>
            <input type="date" v-model="fecha_siniestro" />
          </div>
          <div class="campo">
            <label>Matrícula *</label>
            <input type="text" v-model="matricula" placeholder="0000XXX" @input="matricula = matricula.toUpperCase()" />
          </div>
          <div class="campo">
            <label>Número de expediente <small>(opcional)</small></label>
            <input type="text" v-model="expediente" placeholder="Ej. SD 14106" />
          </div>
          <div class="campo">
            <label>ID expediente EPLAN <small>(opcional)</small></label>
            <input type="text" v-model="expediente_id" placeholder="Ej. 16917" />
          </div>
          <div class="campo campo-ancho">
            <label>Descripción del siniestro (designa) *</label>
            <textarea v-model="descripcion" rows="2" placeholder="Descripción para la designa"></textarea>
          </div>
          <div class="campo campo-ancho">
            <label>Versión del sucedido *</label>
            <textarea v-model="version_suceso" rows="3" placeholder="Relato de lo ocurrido"></textarea>
          </div>
          <div class="campo campo-ancho">
            <label>Observaciones *</label>
            <textarea v-model="observacion" rows="2" placeholder="Observaciones internas"></textarea>
          </div>
        </div>

        <div class="wizard-botonera">
          <ion-button color="medium" fill="outline" @click="step = 1">Atrás</ion-button>
          <ion-button color="dark" :disabled="enviando" @click="enviar">
            {{ enviando ? 'Guardando…' : 'Guardar siniestro' }}
          </ion-button>
        </div>
      </div>

    </div>
  </ion-content>
</template>

<script>
import { defineComponent } from 'vue';
import { close } from 'ionicons/icons';
import { modalController } from '@ionic/vue';
import axios from 'axios';
import toast from '@/plugins/toast';
import { mapGetters } from 'vuex';

const LETRAS_DNI = 'TRWAGMYFPDXBNJZSQVHLCKE';

function afectadoVacio() {
  return { nombre: '', telefono: '', movil: '', dni: '', poblacion: '', direccion: '', num_poliza: '', compania_id: '' };
}

export default defineComponent({
  name: 'AgregarSiniestro',
  computed: {
    ...mapGetters(['getUser']),
    claveBorrador() {
      return 'reclasin_borrador_siniestro_' + (this.getUser?.id || 'anon');
    },
    formulario() {
      return {
        clientes: this.clientes,
        colaborador_id: this.colaborador_id,
        expediente: this.expediente,
        expediente_id: this.expediente_id,
        fecha_siniestro: this.fecha_siniestro,
        matricula: this.matricula,
        descripcion: this.descripcion,
        version_suceso: this.version_suceso,
        observacion: this.observacion,
      };
    },
  },
  setup() {
    return { close };
  },
  data() {
    return {
      step: 1,
      clientes: [afectadoVacio()],
      dniEstado: {},
      origenAutofill: {},
      buscandoDni: null,
      version_suceso: null,
      observacion: null,
      descripcion: null,
      fecha_siniestro: new Date().toISOString().slice(0, 10),
      matricula: '',
      expediente: null,
      expediente_id: null,
      colaboradores: [],
      companias: [],
      colaborador_id: null,
      enviando: false,
      reporteError: null,
      reporteCopiado: false,
      faltan: [],
      borradorDisponible: null,
      guardadoBorradorId: null,
    };
  },
  watch: {
    formulario: {
      deep: true,
      handler() {
        clearTimeout(this.guardadoBorradorId);
        this.guardadoBorradorId = setTimeout(() => this.guardarBorrador(), 800);
      },
    },
  },
  mounted() {
    if (this.getUser?.role?.name == 'Despacho') {
      this.getColaboradoresByUser();
    }
    this.getCompanias();
    this.detectarBorrador();
  },
  methods: {
    async closeModal(data = null) {
      const modal = await modalController;
      return modal.dismiss(data == null ? 'close-button' : data);
    },

    // ---------- afectados ----------
    anadirAfectado() {
      this.clientes.push(afectadoVacio());
    },
    quitarAfectado(idx) {
      this.clientes.splice(idx, 1);
      delete this.dniEstado[idx];
      delete this.origenAutofill[idx];
    },

    // ---------- DNI: validación + autocompletado ----------
    validarDni(valor) {
      if (!valor) return null;
      const v = valor.toUpperCase().trim();
      let numero = null;
      if (/^\d{8}[A-Z]$/.test(v)) {
        numero = parseInt(v.slice(0, 8), 10);
      } else if (/^[XYZ]\d{7}[A-Z]$/.test(v)) {
        numero = parseInt({ X: '0', Y: '1', Z: '2' }[v[0]] + v.slice(1, 8), 10);
      } else {
        return false;
      }
      return LETRAS_DNI[numero % 23] === v[v.length - 1];
    },
    async onDniBlur(idx) {
      const dni = (this.clientes[idx].dni || '').toUpperCase().trim();
      if (!dni) { this.dniEstado[idx] = null; return; }

      const valido = this.validarDni(dni);
      this.dniEstado[idx] = valido;
      if (!valido) return;

      this.buscandoDni = idx;
      try {
        const { data } = await axios.get('/api/buscador/cliente-dni', { params: { dni } });
        const encontrado = data?.data?.cliente;
        if (encontrado) {
          const c = this.clientes[idx];
          // Solo rellena lo que esté vacío: no pisa lo que ya haya escrito el usuario.
          for (const campo of ['nombre', 'telefono', 'movil', 'poblacion', 'direccion', 'num_poliza', 'compania_id']) {
            if (!c[campo] && encontrado[campo]) c[campo] = encontrado[campo];
          }
          this.origenAutofill[idx] = data.data.origen;
        }
      } catch (e) {
        // El autocompletado nunca debe bloquear el alta: si falla, se rellena a mano.
        console.log('autocompletado dni', e);
      } finally {
        this.buscandoDni = null;
      }
    },

    // ---------- catálogo ----------
    getColaboradoresByUser() {
      axios.get('/api/users/colaborador/byUser?all=true')
        .then((res) => {
          this.colaboradores = res.data.data;
          if (this.colaboradores.length === 1) this.colaborador_id = this.colaboradores[0].id;
        })
        .catch((error) => console.log(error));
    },
    getCompanias() {
      axios.get('/api/companias?all=true')
        .then((res) => { this.companias = res.data.data; })
        .catch((error) => console.log(error));
    },

    // ---------- validación local ----------
    validarPaso1() {
      const faltan = [];
      this.clientes.forEach((c, i) => {
        const n = i + 1;
        if (!c.dni) faltan.push(`Afectado ${n}: falta el DNI`);
        else if (this.validarDni(c.dni) === false) faltan.push(`Afectado ${n}: el DNI no es válido (letra incorrecta)`);
        if (!c.nombre) faltan.push(`Afectado ${n}: falta el nombre`);
        if (!c.movil) faltan.push(`Afectado ${n}: falta el móvil`);
        if (!c.telefono) faltan.push(`Afectado ${n}: falta el teléfono`);
        if (!c.num_poliza) faltan.push(`Afectado ${n}: falta el nº de póliza`);
        if (!c.compania_id) faltan.push(`Afectado ${n}: falta la compañía`);
        if (!c.poblacion) faltan.push(`Afectado ${n}: falta la población`);
      });
      return faltan;
    },
    validarPaso2() {
      const faltan = [];
      if (this.getUser?.role?.name == 'Despacho' && !this.colaborador_id) faltan.push('Falta seleccionar el colaborador');
      if (!this.fecha_siniestro) faltan.push('Falta la fecha del siniestro');
      if (!this.matricula) faltan.push('Falta la matrícula');
      if (!this.descripcion) faltan.push('Falta la descripción (designa)');
      if (!this.version_suceso) faltan.push('Falta la versión del sucedido');
      if (!this.observacion) faltan.push('Faltan las observaciones');
      return faltan;
    },
    irAPaso2() {
      this.faltan = this.validarPaso1();
      if (!this.faltan.length) this.step = 2;
    },

    // ---------- envío + informe de error ----------
    async enviar() {
      this.faltan = [...this.validarPaso1(), ...this.validarPaso2()];
      if (this.faltan.length) return;

      this.reporteError = null;
      this.enviando = true;
      const esDespacho = this.getUser?.role?.name == 'Despacho';
      const url = esDespacho ? '/api/siniestros/by/despacho' : '/api/siniestros';

      const payload = {
        clientes: this.clientes,
        version_suceso: this.version_suceso,
        observacion: this.observacion,
        expediente: this.expediente,
        expediente_id: this.expediente_id,
        fecha_siniestro: this.fecha_siniestro,
        matricula: this.matricula,
        descripcion: this.descripcion,
      };
      if (esDespacho) payload.colaborador_id = this.colaborador_id;

      try {
        await axios.post(url, payload);
        localStorage.removeItem(this.claveBorrador);
        toast.openToast('Siniestro registrado correctamente', 'success', 2000);
        this.closeModal('reload');
      } catch (err) {
        this.reporteError = this.construirReporte(err, url);
        // El borrador ya está guardado por el watcher: no se pierde nada.
      } finally {
        this.enviando = false;
      }
    },
    construirReporte(err, url) {
      const ahora = new Date();
      const mensajes = [];
      const resp = err?.response;

      if (!resp) {
        mensajes.push('No hubo respuesta del servidor (fallo de conexión). Comprueba tu conexión e inténtalo de nuevo.');
      } else {
        const msg = resp.data?.error?.message;
        if (Array.isArray(msg)) {
          mensajes.push(...msg);
        } else if (msg && typeof msg === 'object') {
          Object.values(msg).forEach((lista) => mensajes.push(...[].concat(lista)));
        } else if (typeof msg === 'string') {
          mensajes.push(msg);
        } else {
          mensajes.push(`Error ${resp.status} del servidor sin detalle.`);
        }
      }

      return {
        hora: ahora.toLocaleTimeString(),
        mensajes,
        // Texto plano listo para pegar al reportar la incidencia
        texto: [
          `[Reclasin] Error al dar de alta un siniestro`,
          `Fecha: ${ahora.toLocaleString()}`,
          `Usuario: ${this.getUser?.email || '?'} (${this.getUser?.role?.name || '?'})`,
          `Petición: POST ${url} → ${resp ? 'HTTP ' + resp.status : 'sin respuesta'}`,
          `Detalle:`,
          ...mensajes.map((m) => `  - ${m}`),
        ].join('\n'),
      };
    },
    async copiarReporte() {
      try {
        await navigator.clipboard.writeText(this.reporteError.texto);
        this.reporteCopiado = true;
        setTimeout(() => { this.reporteCopiado = false; }, 2000);
      } catch (e) {
        toast.openToast('No se pudo copiar al portapapeles', 'error', 2000);
      }
    },

    // ---------- borrador ----------
    guardarBorrador() {
      const f = this.formulario;
      const hayAlgo = f.clientes.some((c) => Object.values(c).some((v) => v)) ||
        f.matricula || f.descripcion || f.version_suceso || f.observacion || f.expediente;
      if (!hayAlgo) return;
      localStorage.setItem(this.claveBorrador, JSON.stringify({ fecha: Date.now(), datos: f }));
    },
    detectarBorrador() {
      try {
        const crudo = localStorage.getItem(this.claveBorrador);
        if (!crudo) return;
        const b = JSON.parse(crudo);
        this.borradorDisponible = {
          datos: b.datos,
          fechaTexto: new Date(b.fecha).toLocaleString(),
        };
      } catch (e) {
        localStorage.removeItem(this.claveBorrador);
      }
    },
    restaurarBorrador() {
      const d = this.borradorDisponible.datos;
      this.clientes = d.clientes && d.clientes.length ? d.clientes : [afectadoVacio()];
      this.colaborador_id = d.colaborador_id;
      this.expediente = d.expediente;
      this.expediente_id = d.expediente_id;
      this.fecha_siniestro = d.fecha_siniestro;
      this.matricula = d.matricula || '';
      this.descripcion = d.descripcion;
      this.version_suceso = d.version_suceso;
      this.observacion = d.observacion;
      this.borradorDisponible = null;
      this.clientes.forEach((c, i) => { if (c.dni) this.dniEstado[i] = this.validarDni(c.dni); });
    },
    descartarBorrador() {
      localStorage.removeItem(this.claveBorrador);
      this.borradorDisponible = null;
    },
  },
});
</script>

<style scoped>
.wizard-scroll {
  --background: #ffffff;
}

.wizard {
  padding: 20px 24px 24px;
}

.wizard-cabecera {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.wizard-titulo {
  margin: 0 0 4px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.wizard-pasos {
  font-size: 12px;
  color: #9ca3af;
}

.wizard-paso-activo {
  color: #111827;
  font-weight: 700;
}

.wizard-paso-sep {
  margin: 0 6px;
}

.wizard-cerrar {
  border: none;
  background: #111827;
  color: #fff;
  border-radius: 8px;
  width: 30px;
  height: 30px;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.aviso {
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  margin-bottom: 12px;
}

.aviso-borrador {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
}

.aviso-error {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
}

.aviso-faltan {
  background: #fffbeb;
  border: 1px solid #fde68a;
  color: #92400e;
}

.aviso-error-titulo {
  font-weight: 700;
  margin-bottom: 4px;
}

.aviso-error-lista {
  margin: 0 0 6px;
  padding-left: 18px;
}

.aviso-nota {
  font-size: 12px;
  opacity: 0.85;
  margin-bottom: 8px;
}

.aviso-acciones {
  display: flex;
  gap: 8px;
}

.btn-mini {
  border: none;
  background: #111827;
  color: #fff;
  border-radius: 7px;
  padding: 4px 12px;
  font-size: 12px;
  cursor: pointer;
}

.btn-mini-gris {
  background: #e5e7eb;
  color: #374151;
}

.tarjeta-afectado {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
  background: #fafafa;
}

.tarjeta-cabecera {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}

.tarjeta-titulo {
  font-weight: 700;
  font-size: 14px;
  color: #111827;
  flex: 1;
}

.chip-autofill {
  background: #ecfdf5;
  color: #047857;
  border: 1px solid #a7f3d0;
  border-radius: 999px;
  font-size: 11px;
  padding: 2px 10px;
}

.grid-campos {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 14px;
}

.campo-ancho {
  grid-column: 1 / -1;
}

.campo label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 3px;
  text-align: left;
}

.campo input,
.campo select,
.campo textarea {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  background: #fff;
  box-sizing: border-box;
  color: #111827;
}

.campo input:focus,
.campo select:focus,
.campo textarea:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.campo-invalido {
  border-color: #ef4444 !important;
}

.campo-dni {
  display: flex;
  align-items: center;
  gap: 8px;
}

.dni-ok { color: #059669; font-weight: 700; }
.dni-mal { color: #dc2626; font-size: 12px; white-space: nowrap; }
.dni-buscando { color: #6b7280; font-size: 12px; }

.btn-anadir {
  border: 1px dashed #9ca3af;
  background: transparent;
  color: #374151;
  border-radius: 10px;
  width: 100%;
  padding: 9px;
  font-size: 13px;
  cursor: pointer;
  margin-bottom: 6px;
}

.btn-anadir:hover { background: #f3f4f6; }

.wizard-botonera {
  display: flex;
  justify-content: space-between;
  margin-top: 16px;
}

ion-button {
  --border-radius: 10px;
  text-transform: none;
}

@media (max-width: 640px) {
  .grid-campos { grid-template-columns: 1fr; }
}
</style>
